/**
 * A charge plan in the spec and the draft (L23 of docs/plans/ledger.md, step
 * 12.1: charge plans drafted by setup). A business that bills its parties on
 * a schedule (school fees, a gym's membership, rent) carries the plan on its
 * party list: what each party is billed, to which income, and how often.
 *
 * Apply turns the mark into the two rules of a run (`ledger/charge-plan.ts`):
 * it makes the Charges record the run writes into (`makeChargesRecord`, with
 * its ordinary `charge` rule), puts the plan on the list
 * (`installChargePlan`), and marks the list a list of parties with its
 * joining and leaving days (`markPartyList`). The setup never writes the
 * Charges record itself, as it never writes posting lines.
 *
 *   name               the Charges record's name, in the business's words
 *   partyLabel         the label of the Charges record's pick of the list
 *   cadence            monthly, quarterly or yearly
 *   lines              one to three: the label of the charge's amount on the
 *                      Charges record, the amount (a money field of the list,
 *                      or a fixed amount) and its income account (Sales when
 *                      left out)
 *   joinedOnFieldKey   a date field of the list: a party is billed from it
 *   leftOnFieldKey     a date field of the list: and not after it
 *   discountFieldKey   a number field of the list: a percent off each charge,
 *                      or, with the keys below, the discount's value
 *
 * A discount by cycle (B1 of docs/plans/gym-membership.md), any party list's
 * (a gym's members, a school's students, a society's flats):
 *
 *   discountKindFieldKey    a select of the list whose choices carry the keys
 *                           `amount` and `percent`: what discountFieldKey
 *                           holds. Left out: a percent, as before.
 *   discountForFieldKey     a select whose choices carry the keys `first`,
 *                           `cycles` and `every`: how long the discount lasts.
 *                           Left out: every charge, as before.
 *   discountCyclesFieldKey  a number field: how many cycles, this one
 *                           included, for `cycles`
 *   discountFromFieldKey    a date field: the cycle is counted by period from
 *                           the period that holds it (G9)
 *
 * `chargePlanProblems` refuses a plan apply could not install. Pure.
 */
import type { ChargeCadence } from '../api';
import { isChargeCadence, type FirstPeriod } from '../ledger/charge-periods';
import { isLedgerSystemKey } from '../ledger/patterns';
import type { RuleProblem, RuleRecord } from './posting';

export type SpecChargeLine = {
  readonly label: string;
  readonly amount: { readonly fieldKey: string } | { readonly const: number };
  /** The income account: a system key, or a row of the Accounts list by its name. Sales when absent. */
  readonly income?: { readonly systemKey: string } | { readonly row: string };
};

export type SpecChargePlan = {
  readonly name: string;
  readonly partyLabel: string;
  readonly cadence: ChargeCadence;
  readonly firstPeriod?: FirstPeriod;
  readonly lines: readonly SpecChargeLine[];
  readonly joinedOnFieldKey?: string;
  readonly leftOnFieldKey?: string;
  readonly discountFieldKey?: string;
  /** B1: a select keyed `amount` and `percent`; left out, the discount is a percent. */
  readonly discountKindFieldKey?: string;
  /** B1: a select keyed `first`, `cycles` and `every`; left out, every charge. */
  readonly discountForFieldKey?: string;
  /** B1: a number field, the cycles (this one included) for `cycles`. */
  readonly discountCyclesFieldKey?: string;
  /** B1: a date field, the day the cycles are counted from. */
  readonly discountFromFieldKey?: string;
};

/** The DISCOUNT_* choice keys a discount by cycle reads (B1 of docs/plans/gym-membership.md). */
export const DISCOUNT_KIND_KEYS = ['amount', 'percent'] as const;
export const DISCOUNT_FOR_KEYS = ['first', 'cycles', 'every'] as const;
export type DiscountKind = (typeof DISCOUNT_KIND_KEYS)[number];
export type DiscountFor = (typeof DISCOUNT_FOR_KEYS)[number];

/** The keys of a plan that name a field of its list, besides the lines' amounts. */
const PLAN_FIELD_KEYS = [
  'joinedOnFieldKey',
  'leftOnFieldKey',
  'discountFieldKey',
  'discountKindFieldKey',
  'discountForFieldKey',
  'discountCyclesFieldKey',
  'discountFromFieldKey',
] as const;

/** Sales: the income a charge line names when it names none. */
export const CHARGE_INCOME_DEFAULT = { systemKey: 'sales' } as const;

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);
const word = (value: unknown, max: number): string | undefined => (typeof value === 'string' && value.trim() !== '' && value.trim().length <= max ? value.trim() : undefined);

const readLine = (raw: unknown, at: string): SpecChargeLine | string => {
  if (!isObject(raw)) return `${at} as { label, amount, income? }`;
  const label = word(raw.label, 80);
  if (label === undefined) return `${at}.label as the charge's words`;
  const amount = raw.amount;
  let read: SpecChargeLine['amount'];
  if (isObject(amount) && word(amount.fieldKey, 64) !== undefined) read = { fieldKey: word(amount.fieldKey, 64)! };
  else if (isObject(amount) && typeof amount.const === 'number' && Number.isFinite(amount.const) && amount.const > 0) read = { const: amount.const };
  else return `${at}.amount as { fieldKey } or { const } above zero`;
  let income: SpecChargeLine['income'];
  if (raw.income !== undefined && raw.income !== null) {
    const one = raw.income;
    if (isObject(one) && word(one.systemKey, 64) !== undefined) income = { systemKey: word(one.systemKey, 64)! };
    else if (isObject(one) && word(one.row, 80) !== undefined) income = { row: word(one.row, 80)! };
    else return `${at}.income as { systemKey } or { row }`;
  }
  return { label, amount: read, ...(income === undefined ? {} : { income }) };
};

/** A charge plan read off the wire (an op, a composed setup), or a sentence saying what is wrong with its shape. What it MEANS is `chargePlanProblems`'. */
export const readSpecChargePlan = (raw: unknown): SpecChargePlan | string => {
  if (!isObject(raw)) return 'an object with name, partyLabel, cadence and lines';
  const name = word(raw.name, 80);
  if (name === undefined) return "name as the Charges record's name";
  const partyLabel = word(raw.partyLabel, 80);
  if (partyLabel === undefined) return "partyLabel as the words of the charge's pick of the list";
  if (!isChargeCadence(raw.cadence)) return 'cadence as monthly, quarterly or yearly';
  if (raw.firstPeriod !== undefined && raw.firstPeriod !== null && raw.firstPeriod !== 'full' && raw.firstPeriod !== 'by_days') return 'firstPeriod as full or by_days';
  if (!Array.isArray(raw.lines) || raw.lines.length < 1 || raw.lines.length > 3) return 'lines as one to three charge lines';
  const lines: SpecChargeLine[] = [];
  for (const [index, one] of raw.lines.entries()) {
    const read = readLine(one, `lines[${index}]`);
    if (typeof read === 'string') return read;
    lines.push(read);
  }
  const keys: Record<string, string> = {};
  for (const member of PLAN_FIELD_KEYS) {
    if (raw[member] === undefined || raw[member] === null) continue;
    const key = word(raw[member], 64);
    if (key === undefined) return `${member} as a field key`;
    keys[member] = key;
  }
  return {
    name,
    partyLabel,
    cadence: raw.cadence,
    ...(raw.firstPeriod === 'full' || raw.firstPeriod === 'by_days' ? { firstPeriod: raw.firstPeriod } : {}),
    lines,
    ...keys,
  };
};

/**
 * The left-out mark a list keeps once its charge plan was taken off (L65:
 * "When they buy a plan"): the setup never drafts a plan there again from
 * the person's earlier words.
 */
export const chargePlanLeftOf = (listKey: string): string => `charge_plan:${listKey}`;

/** A field that reaches a person: a list with one is a list of people. */
const REACHES = new Set(['phone', 'email']);

/** The lists a money rule names as who pays or is paid: the targets of a `party` role read from a field. */
const partyLists = (records: readonly RuleRecord[]): Set<string> => {
  const out = new Set<string>();
  for (const record of records) {
    const party = record.posting?.roles?.party;
    if (party === undefined || party === null || typeof party !== 'object' || !('fieldKey' in party)) continue;
    const target = record.fields.find((field) => field.key === (party as { fieldKey: string }).fieldKey)?.targetRecordKey;
    if (target !== undefined) out.add(target);
  }
  return out;
};

/**
 * Whether a list is what the parties buy, never who pays (L65): no money rule
 * names it as the party, it keeps no phone or email, and a record picks it
 * beside a list that is one of those (FitTek, 30 Oct: Memberships picks Plans
 * beside Members, and a plan on Plans billed Monthly, Quarterly and Yearly as
 * if they owed).
 */
export const soldNotBilled = (record: RuleRecord, records: readonly RuleRecord[]): boolean => {
  const parties = partyLists(records);
  const people = (one: RuleRecord): boolean => parties.has(one.key) || one.fields.some((field) => REACHES.has(field.type));
  if (people(record)) return false;
  const payers = new Set(records.filter((one) => one.key !== record.key && one.kind === 'directory' && people(one)).map((one) => one.key));
  return records.some(
    (one) =>
      one.kind === 'record' &&
      one.fields.some((field) => field.targetRecordKey === record.key) &&
      one.fields.some((field) => field.targetRecordKey !== undefined && payers.has(field.targetRecordKey)),
  );
};

/**
 * Every problem with a list's charge plan, or none. `records` are the spec's
 * (or the draft's): the plan needs the Accounts list, since a charge posts to
 * what customers owe and to an income row. `accountRows` are the words of
 * the Accounts rows' names, lower case; absent skips the `{ row }` check.
 */
export const chargePlanProblems = (
  plan: SpecChargePlan,
  record: RuleRecord,
  records: readonly RuleRecord[],
  at: string,
  accountRows?: ReadonlySet<string>,
): RuleProblem[] => {
  const problems: RuleProblem[] = [];
  const say = (code: string, message: string): void => void problems.push({ code, message, path: at });
  if (record.kind !== 'directory') say('CHARGE_PLAN_NOT_LIST', `"${record.key}" is a record; a charge plan sits on the list of the parties it bills.`);
  else if (soldNotBilled(record, records)) say('CHARGE_PLAN_NOT_PARTIES', `"${record.key}" is what the parties buy, not who pays; a charge plan sits on the list of the parties it bills.`);
  // The setup's money pass brings the Accounts list in for a plan: the studio holds this as unfinished, and Build refuses it.
  if (!records.some((one) => one.accounts !== undefined)) say('CHARGE_PLAN_NO_ACCOUNTS', 'A charge plan counts its money in the Accounts list, and the setup holds none.');
  if (!isChargeCadence(plan.cadence)) say('CHARGE_PLAN_SHAPE', 'A charge plan bills monthly, quarterly or yearly.');
  if (plan.lines.length < 1 || plan.lines.length > 3) say('CHARGE_PLAN_SHAPE', 'A charge plan bills one to three amounts.');
  const labels = new Set<string>();
  const fieldOf = (key: string, wants: readonly string[], what: string, role: string): void => {
    const field = record.fields.find((one) => one.key === key);
    if (field === undefined) say('RULE_FIELD_UNKNOWN', `The charge plan's ${role} reads "${key}", which is no field of "${record.key}".`);
    else if (!wants.includes(field.type)) say('RULE_FIELD_TYPE', `The charge plan's ${role} reads "${key}", a ${field.type} field; it needs ${what}.`);
  };
  for (const line of plan.lines) {
    const label = line.label.trim().toLowerCase();
    if (labels.has(label)) say('CHARGE_PLAN_SHAPE', `Two of the charge's amounts are called "${line.label}".`);
    labels.add(label);
    if ('fieldKey' in line.amount) fieldOf(line.amount.fieldKey, ['money', 'number', 'formula', 'rollup'], 'a money or number field', `amount "${line.label}"`);
    else if (!(line.amount.const > 0)) say('CHARGE_PLAN_SHAPE', `The charge "${line.label}" bills nothing.`);
    const income = line.income ?? CHARGE_INCOME_DEFAULT;
    if ('systemKey' in income && !isLedgerSystemKey(income.systemKey)) say('RULE_ACCOUNT_UNKNOWN', `The charge "${line.label}" names the system key "${income.systemKey}", which no account carries.`);
    if ('row' in income && accountRows !== undefined && !accountRows.has(income.row.trim().toLowerCase())) {
      say('RULE_ACCOUNT_UNKNOWN', `The charge "${line.label}" names the account "${income.row}", which the Accounts list does not hold.`);
    }
  }
  if (plan.joinedOnFieldKey !== undefined) fieldOf(plan.joinedOnFieldKey, ['date'], 'a date field', 'joining day');
  if (plan.leftOnFieldKey !== undefined) fieldOf(plan.leftOnFieldKey, ['date'], 'a date field', 'leaving day');
  // With a kind, the value may be an amount: a money field too.
  if (plan.discountFieldKey !== undefined) {
    fieldOf(plan.discountFieldKey, plan.discountKindFieldKey === undefined ? ['number', 'formula'] : ['number', 'money', 'formula'], 'a number field', 'discount');
  }
  // A discount by cycle (B1 of docs/plans/gym-membership.md): each key reads the discount's value, and its selects carry the keys the run reads.
  const byCycle = [plan.discountKindFieldKey, plan.discountForFieldKey, plan.discountCyclesFieldKey, plan.discountFromFieldKey].some((key) => key !== undefined);
  if (byCycle && plan.discountFieldKey === undefined) say('CHARGE_PLAN_SHAPE', "The charge plan's discount by cycle names no discount (discountFieldKey).");
  const choicesOf = (key: string, wants: readonly string[], role: string): void => {
    const field = record.fields.find((one) => one.key === key);
    fieldOf(key, ['select'], 'a select', role);
    if (field === undefined || field.type !== 'select') return;
    const held = new Set((field.options ?? []).map((one) => one.key));
    const missing = wants.filter((one) => !held.has(one));
    if (missing.length > 0) say('CHARGE_PLAN_SHAPE', `The charge plan's ${role} reads "${key}", whose choices carry no key ${missing.map((one) => `"${one}"`).join(', ')}.`);
  };
  if (plan.discountKindFieldKey !== undefined) choicesOf(plan.discountKindFieldKey, DISCOUNT_KIND_KEYS, 'discount kind');
  if (plan.discountForFieldKey !== undefined) {
    choicesOf(plan.discountForFieldKey, DISCOUNT_FOR_KEYS, 'discount for');
    // G9: the cycle is counted from a day, and "a number of cycles" needs the number.
    if (plan.discountFromFieldKey === undefined) say('CHARGE_PLAN_SHAPE', "The charge plan's discount for some cycles names no day to count them from (discountFromFieldKey).");
    if (plan.discountCyclesFieldKey === undefined) say('CHARGE_PLAN_SHAPE', "The charge plan's discount for some cycles names no number of cycles (discountCyclesFieldKey).");
  } else if (plan.discountCyclesFieldKey !== undefined || plan.discountFromFieldKey !== undefined) {
    say('CHARGE_PLAN_SHAPE', "The charge plan names cycles for its discount but not how long it lasts (discountForFieldKey).");
  }
  if (plan.discountCyclesFieldKey !== undefined) fieldOf(plan.discountCyclesFieldKey, ['number', 'formula'], 'a number field', 'discount cycles');
  if (plan.discountFromFieldKey !== undefined) fieldOf(plan.discountFromFieldKey, ['date', 'formula'], 'a date field', 'discount from');
  return problems;
};

/** The field keys a charge plan reads on its list: what removing a field must not leave dangling. */
export const chargePlanFieldKeys = (plan: SpecChargePlan): string[] => [
  ...plan.lines.flatMap((line) => ('fieldKey' in line.amount ? [line.amount.fieldKey] : [])),
  ...PLAN_FIELD_KEYS.map((member) => plan[member]).filter((key): key is string => key !== undefined),
];
